import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { useUser } from "../components/auth-provider";

// user signup form schema
const formSchema = z.object({
	firstName: z.string().min(2, {
		message: "First name must be at least 2 characters.",
	}),
	lastName: z.string().min(2, {
		message: "Last name must be at least 2 characters.",
	}),
	email: z.string().email({
		message: "Please enter a valid email address.",
	}),
	password: z.string().min(8, {
		message: "Password must be at least 8 characters.",
	}),
	phoneNumber: z.string().optional(),
	acceptTerms: z.boolean().refine((val) => val === true, {
		message: "Accept Terms of Use to continue.",
	}),
});

export function Signup() {
	const navigate = useNavigate();

	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	const { fetchUser } = useUser();

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			password: "",
			phoneNumber: "",
			acceptTerms: false,
		},
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setLoading(true);

		const data: any = await api.post("api/v1/auth/signup", {
			name: `${values.firstName} ${values.lastName}`,
			email: values.email,
			password: values.password,
			phone: values.phoneNumber,
		});

		if (!data.success) {
			setLoading(false);
			toast.error(data.message ?? "Failed to signup the user");
			return;
		}

		localStorage.setItem("authToken", data.message.token);
		fetchUser();
		navigate("/");
		toast.success("Successfully logged in");

		setLoading(false);
	}

	return (
		<div className="py-4 px-4">
			<div className="flex flex-col gap-4 content-stretch">
				<div className="self-stretch">
					<h2 className="text-lg font-bold mt-3">Complete registration to access</h2>
				</div>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 self-stretch">
						<FormField
							control={form.control}
							name="firstName"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormLabel className="font-medium text-sm">
										First name <span className="text-rose-500">*</span>
									</FormLabel>
									<FormControl>
										<Input {...field} className="flex h-10 p-2 items-center rounded-md" />
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="lastName"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormLabel className="font-medium text-sm">
										Last name <span className="text-rose-500">*</span>
									</FormLabel>
									<FormControl>
										<Input {...field} className="flex h-10 p-2 items-center rounded-md" />
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormLabel className="font-medium text-sm">
										Email <span className="text-rose-500">*</span>
									</FormLabel>
									<FormControl>
										<Input {...field} className="flex h-10 p-2 items-center rounded-md" />
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormLabel className="font-medium text-sm">
										Password <span className="text-rose-500">*</span>
									</FormLabel>
									<FormControl>
										<div className="relative">
											<Input
												type={showPassword ? "text" : "password"}
												{...field}
												className="flex h-10 p-2 items-center rounded-md"
											/>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="absolute right-2 top-0 h-full text-gray-500 hover:bg-transparent cursor-pointer"
												onClick={() => setShowPassword(!showPassword)}
											>
												{showPassword ? <EyeOff className="size-6" /> : <Eye className="size-6" />}
											</Button>
										</div>
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="phoneNumber"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormLabel className="font-medium text-sm">
										Phone number <span className="text-gray-500">(Optional)</span>
									</FormLabel>
									<FormControl>
										<Input {...field} className="flex h-10 p-2 items-center rounded-md" />
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="acceptTerms"
							render={({ field }) => (
								<FormItem className="flex gap-x-2 items-center">
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={field.onChange}
											className="data-[state=checked]:bg-primary size-4"
										/>
									</FormControl>
									<FormLabel className="font-medium text-sm">
										I accept the
										<a href="https://legalgraph.ai/terms-of-use" target="_blank" rel="noopener noreferrer">
											<span className="text-primary underline">Terms of Use</span>
										</a>
										and
										<a href="https://legalgraph.ai/privacy-policy" target="_blank" rel="noopener noreferrer">
											<span className="text-primary underline">Privacy policy</span>
										</a>
									</FormLabel>
								</FormItem>
							)}
						/>

						<Button type="submit" disabled={loading}>
							{loading ? (
								<>
									<Loader2 className="size-4 animate-spin mr-2" />
									Signing up...
								</>
							) : (
								"Sign up"
							)}
						</Button>
					</form>
				</Form>
			</div>
			<p className="mt-1 flex items-center justify-center w-screen text-gray-500 text-sm">
				Already a user?&nbsp;
				<strong className="text-primary underline">
					<Link to="/login">Log In</Link>
				</strong>
			</p>
		</div>
	);
}
